import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Camera, Calculator, Megaphone, ChevronRight, CheckCircle, Sparkles, ArrowRight } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Stage Card Component with Image
const StageCard = ({ stage, title, icon: Icon, description, features, color, image, isReversed }) => (
  <div className={`grid lg:grid-cols-2 gap-8 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
    {/* Image Side */}
    <div className={`relative ${isReversed ? 'lg:order-2' : ''}`}>
      <div 
        className="rounded-2xl overflow-hidden"
        style={{ boxShadow: `0 20px 60px ${color}30` }}
      >
        <img 
          src={image} 
          alt={title}
          className="w-full h-80 object-cover"
        />
      </div>
      {/* Stage Badge */}
      <div 
        className="absolute -top-4 -left-4 w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-2xl z-10"
        style={{ 
          background: color,
          color: "white",
          boxShadow: `0 10px 30px ${color}50`
        }}
      >
        {stage}
      </div>
    </div>
    
    {/* Content Side */}
    <div className={isReversed ? 'lg:order-1' : ''}>
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="p-3 rounded-xl"
          style={{ background: `${color}20` }}
        >
          <Icon size={28} style={{ color: color }} />
        </div>
        <h3 
          className="text-3xl md:text-4xl font-bold"
          style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
        >
          {title}
        </h3>
      </div>
      
      <p 
        className="mb-6 text-lg"
        style={{ color: "var(--text-200)", lineHeight: "1.7" }}
      >
        {description}
      </p>
      
      <ul className="space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <CheckCircle size={20} style={{ color: color, flexShrink: 0, marginTop: "2px" }} />
            <span style={{ color: "var(--text-200)" }}>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

// Main How It Works Page Component
const HowItWorksPage = () => {
  const stages = [
    {
      stage: "A",
      title: "Appraisal",
      icon: Camera,
      color: "#0038a8",
      image: "https://www.machinerymarketed.com/app-appraisal.jpg",
      description: "The process starts with the salesperson. Using MachineryMarketed, the salesperson is guided to capture all required information about the trade.",
      features: [
        "Machine details & specifications",
        "Condition assessment & photos",
        "Hour meter readings",
        "Customer expectations",
        "All value-influencing factors captured",
        "No scraps of paper — everything digital"
      ]
    },
    {
      stage: "B",
      title: "Valuation",
      icon: Calculator,
      color: "#0049db",
      image: "https://www.machinerymarketed.com/app-valuation.jpg",
      description: "With the press of a button, a clear and concise PDF is sent to the person responsible for valuing trades within your dealership.",
      features: [
        "PDF report sent to valuation manager",
        "AI scans market for comparable machines",
        "Consistent calculation support",
        "Removes guesswork from decisions",
        "Faster turnaround on approvals",
        "Higher likelihood of winning deals"
      ]
    },
    {
      stage: "C",
      title: "Marketing",
      icon: Megaphone,
      color: "#0f5fff",
      image: "https://www.machinerymarketed.com/app-machine-details.jpg",
      description: "Once the deal is done, this is where MachineryMarketed really saves time. Using the photos and information already captured, the machine is prepared for market.",
      features: [
        "One-click professional listings",
        "Push to websites & platforms",
        "Social media integration",
        "No double-handling of data",
        "Reach market sooner",
        "Team stays focused on customers"
      ]
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.25 }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.7) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-16">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>The Process</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                How It <span className="text-gradient">Works</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed follows a simple, three-stage process that mirrors how dealerships 
                already operate — just <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>cleaner, faster, and more consistent</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Process Flow Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            {/* Arrow Flow Indicator (Desktop) */}
            <div className="hidden lg:flex justify-center items-center gap-4 mb-16">
              {['Appraisal', 'Valuation', 'Marketing'].map((step, index) => (
                <div key={step} className="flex items-center gap-4">
                  <div 
                    className="px-6 py-3 rounded-full font-bold"
                    style={{ 
                      background: index === 0 ? '#0038a8' : index === 1 ? '#0049db' : '#0f5fff',
                      color: "white"
                    }}
                  >
                    {step}
                  </div>
                  {index < 2 && <ArrowRight size={32} style={{ color: "var(--primary-300)" }} />}
                </div>
              ))}
            </div>
            
            {/* Stage Cards */}
            <div className="space-y-24 max-w-6xl mx-auto">
              {stages.map((stage, index) => (
                <StageCard 
                  key={index} 
                  {...stage} 
                  isReversed={index % 2 === 1}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section with Image Background */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/agri-6.jpg" 
              alt="Equipment yard"
              className="w-full h-full object-cover"
              style={{ opacity: 0.1 }}
            />
          </div>
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                What Makes the Difference
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div 
                  className="card p-8"
                  style={{ background: "rgba(255, 100, 100, 0.1)", borderColor: "rgba(255, 100, 100, 0.2)" }}
                >
                  <h3 
                    className="text-2xl font-bold mb-6 flex items-center gap-3"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "#ff6464" }}
                  >
                    <span className="text-3xl">✗</span> Traditional Process
                  </h3>
                  <ul className="space-y-4" style={{ color: "var(--text-300)" }}>
                    <li className="flex items-center gap-3">
                      <span>•</span> Paper forms and scattered notes
                    </li>
                    <li className="flex items-center gap-3">
                      <span>•</span> Chasing photos and information later
                    </li>
                    <li className="flex items-center gap-3">
                      <span>•</span> Manual data entry into multiple systems
                    </li>
                    <li className="flex items-center gap-3">
                      <span>•</span> Delayed time-to-market
                    </li>
                    <li className="flex items-center gap-3">
                      <span>•</span> Machines become "forgotten hostage cash"
                    </li>
                  </ul>
                </div>
                
                <div 
                  className="card p-8"
                  style={{ 
                    background: "rgba(15, 95, 255, 0.1)", 
                    borderColor: "rgba(15, 95, 255, 0.3)",
                    borderWidth: "2px"
                  }}
                >
                  <h3 
                    className="text-2xl font-bold mb-6 flex items-center gap-3"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
                  >
                    <CheckCircle size={28} /> With MachineryMarketed
                  </h3>
                  <ul className="space-y-4" style={{ color: "var(--text-200)" }}>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: "var(--primary-300)" }} /> Guided digital appraisal
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: "var(--primary-300)" }} /> Everything captured at point of trade
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: "var(--primary-300)" }} /> One-click marketing to all channels
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: "var(--primary-300)" }} /> Faster time-to-market
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: "var(--primary-300)" }} /> Better visibility, fewer machines lost
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden"
              style={{ maxWidth: "900px", margin: "0 auto" }}
            >
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="https://www.machinerymarketed.com/hero_excavator.jpg" 
                  alt="Equipment"
                  className="w-full h-full object-cover"
                  style={{ opacity: 0.3 }}
                />
                <div 
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)" }}
                />
              </div>
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Ready to streamline your workflow?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  See how MachineryMarketed can transform your dealership.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/faq" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "transparent", 
                      border: "2px solid white",
                      color: "white"
                    }}
                  >
                    Read FAQ
                  </Link>
                  <Link 
                    to="/#contact" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "white", 
                      color: "var(--primary-100)"
                    }}
                  >
                    Request a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
